import { useState, useEffect } from 'react';
import axios from 'axios';
import { format, eachDayOfInterval, startOfMonth, endOfMonth } from 'date-fns';

const ViewAttendance = () => {
  const storedSessionId = localStorage.getItem('currentSessionId') || '';
  const [currentMonth] = useState(new Date());
  const [searchQuery, setSearchQuery] = useState('');
  const [students, setStudents] = useState([]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [error, setError] = useState(null);

  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState('');

  const labelStyle = "font-semibold text-[18px] text-black mb-1";
  const inputStyle =
    "px-3 py-2 border border-gray-300 rounded-lg shadow-sm bg-white text-[14px] text-gray-800 placeholder:text-[14px] placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500";

  // Fetch classes
  useEffect(() => {
    if (!storedSessionId) return;

    axios.get(` ${process.env.REACT_APP_API_BASE_URL}/api/classes/getClasses?sessionId=${storedSessionId}`)
      .then(res => setClasses(res.data))
      .catch(err => setError(err.message));
  }, [storedSessionId]);

  // Fetch sections when class changes
  useEffect(() => {
    if (!selectedClass) {
      setSections([]);
      setSelectedSection('');
      return;
    }

    const className = classes.find(c => c._id === selectedClass)?.className;
    if (!className) return;

    axios.get(` ${process.env.REACT_APP_API_BASE_URL}/api/sections/${encodeURIComponent(className)}/getSectionsByClassName?sessionId=${storedSessionId}`)
      .then(res => setSections(res.data || []))
      .catch(err => setError(err.message));
  }, [selectedClass, classes, storedSessionId]);

  // Fetch students and attendance
  useEffect(() => {
    if (!selectedClass || !selectedSection) return;

    const fetchData = async () => {
      try {
        const className = classes.find(c => c._id === selectedClass)?.className;
        if (!className) return;

        // Fetch students
        const stuRes = await axios.get(` ${process.env.REACT_APP_API_BASE_URL}/api/students/getStudents`, {
          params: { class: className, section: selectedSection, sessionId: storedSessionId, status: "active" }
        });
        setStudents(stuRes.data || []);

        // Fetch attendance and populate names
        const attRes = await axios.get(` ${process.env.REACT_APP_API_BASE_URL}/api/attendance/getAttendance`);
        const attendanceWithNames = attRes.data.map(record => ({
          ...record,
          students: record.students.map(s => ({
            ...s,
            studentId: s.studentId || { _id: s._id, firstName: 'Unknown', lastName: '', rollNo: s.rollNo } // fallback
          }))
        }));
        setAttendanceData(attendanceWithNames || []);
      } catch (err) {
        setError(err.message);
      }
    };

    fetchData();
  }, [selectedClass, selectedSection, classes, storedSessionId]);

  const daysOfMonth = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth),
  });

  const getStatusForDay = (student, day) => {
    const record = attendanceData.find(att =>
      att.students.some(s => (s.studentId._id || s.studentId) === student._id &&
        format(new Date(s.date), 'yyyy-MM-dd') === format(day, 'yyyy-MM-dd'))
    );

    if (!record) return '-';

    const studentAtt = record.students.find(s => (s.studentId._id || s.studentId) === student._id);
    if (!studentAtt) return '-';

    switch (studentAtt.status) {
      case 'Present': return <span className="text-green-600 font-bold">P</span>;
      case 'Absent': return <span className="text-red-600 font-bold">A</span>;
      case 'Late': return <span className="text-orange-600 font-bold">L</span>;
      case 'onLeave': return <span className="text-blue-600 font-bold">OL</span>;
      default: return '-';
    }
  };

  const filteredStudents = students.filter(student =>
    (!selectedClass || student.class === classes.find(c => c._id === selectedClass)?.className) &&
    (!selectedSection || student.section === selectedSection) &&
    ((student.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      student.lastName?.toLowerCase().includes(searchQuery.toLowerCase())) ||
      String(student.rollNo ?? '').includes(searchQuery))
  );

  const noResults = searchQuery && filteredStudents.length === 0;

  return (
    <div className="p-4 md:p-6 bg-white shadow-md rounded-lg max-w-full mx-auto">
      {/* FILTERS */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="flex-1">
          <label className={labelStyle}>Search:</label>
          <input
            type="text"
            placeholder="Search by name or roll number..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`${inputStyle} w-full`}
          />
        </div>

        <div>
          <label className={labelStyle}>Class:</label>
          <select
            value={selectedClass}
            onChange={(e) => setSelectedClass(e.target.value)}
            className={inputStyle}
          >
            <option value="">Choose class...</option>
            {classes.map(cls => <option key={cls._id} value={cls._id}>{cls.className}</option>)}
          </select>
        </div>

        <div>
          <label className={labelStyle}>Section:</label>
          <select
            value={selectedSection}
            onChange={(e) => setSelectedSection(e.target.value)}
            disabled={!selectedClass}
            className={inputStyle}
          >
            <option value="">Choose section...</option>
            {sections.map(sec => <option key={sec._id} value={sec.sectionName}>{sec.sectionName}</option>)}
          </select>
        </div>
      </div>

      {noResults && <p className="text-red-500 mb-4 text-[14px]">No students found matching "{searchQuery}".</p>}
      {error && <p className="text-red-500 mb-4 text-[14px]">{error}</p>}

      {/* TABLE */}
      <div className="overflow-x-auto">
        <table className="min-w-full border border-gray-300 text-center">
          <thead className="bg-gray-200">
            <tr>
              <th className="py-2 px-2 md:px-4 border-b text-[18px] font-semibold text-black">#</th>
              <th className="py-2 px-2 md:px-4 border-b text-[18px] font-semibold text-black">Student Name</th>
              <th className="py-2 px-2 md:px-4 border-b text-[18px] font-semibold text-black">Roll Number</th>
              {daysOfMonth.map(day => (
                <th key={day} className="py-2 px-1 md:px-2 border-b text-[14px] font-semibold text-black">
                  {format(day, 'dd')}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="text-center text-[14px]">
            {filteredStudents.length > 0 ? filteredStudents.map((student, index) => (
              <tr key={student._id} className="hover:bg-gray-50">
                <td className="py-1 px-2 md:px-4 border-b bg-gray-100">{index + 1}</td>
                <td className="py-1 px-2 md:px-4 border-b">{student.firstName} {student.lastName}</td>
                <td className="py-1 px-2 md:px-4 border-b bg-gray-100">{student.rollNo}</td>
                {daysOfMonth.map(day => (
                  <td key={day} className="py-1 px-1 md:px-2 border-b">{getStatusForDay(student, day)}</td>
                ))}
              </tr>
            )) : (
              <tr>
                <td colSpan={daysOfMonth.length + 3} className="py-4 text-center text-[14px]">
                  No students available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ViewAttendance;
